import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import { SelectedTargetItem } from "../../types";
import { GroupListItem, useGroupsList } from "../../hooks/useGroupsList";
import GroupRow from "./rows/GroupRow";

const PAGE_SIZE = 10;

interface GroupsTabProps {
  corporateId: string | number;
  selected: SelectedTargetItem[];
  onChange: (next: SelectedTargetItem[]) => void;
}

const GroupsTab: React.FC<GroupsTabProps> = ({
  corporateId,
  selected,
  onChange,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading } = useGroupsList({
    corporateId,
    page,
    pageSize,
    search: search || undefined,
    fieldFilters,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((g) => String(g.id) === String(id));
      if (!found) return;
      onChange([
        ...selected,
        {
          id: found.id,
          label: found.name || `Gruppo ${found.id}`,
          groupType: found.type,
          meta: found,
        },
      ]);
    } else {
      onChange(selected.filter((s) => String(s.id) !== String(id)));
    }
  };

  return (
    <SearchableMultiSelectListCst<GroupListItem>
      items={items}
      getId={(g) => g.id}
      renderRow={({ item }) => <GroupRow group={item} />}
      selectedIds={selectedSet}
      onToggle={handleToggle}
      initialSearchValue={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      searchPlaceholder={t("targets.groups.search-placeholder") || "Nome"}
      searchButtonLabel={t("targets.search-button") || "Cerca"}
      hideSearch
      headerSlot={
        <FiltersAccordionToggle
          pageId={PagesID["corporate.groups"]}
          value={fieldFilters}
          onApply={(next) => {
            setFieldFilters(next);
            setPage(1);
          }}
        />
      }
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
      isLoading={isLoading}
      selectedCountLabel={
        t("targets.selected-count", { count: selected.length }) ||
        `${selected.length} elementi selezionati`
      }
      emptyLabel={t("targets.groups.empty") || "Nessun gruppo trovato"}
    />
  );
};

export default GroupsTab;
